<script setup>
import { ref, watch, useId, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import DealTimeline from './DealTimeline.vue';
import DealStatistics from './DealStatistics.vue';
import DealMedia from './DealMedia.vue';

const props = defineProps({ deal: { type: Object, required: true } });
const { t } = useI18n();
const { accountId } = useAccount();
const id = useId();
const slots = useSlots();
const tabs = [
  ...(slots.principal ? ['PRINCIPAL'] : []),
  'HISTORY',
  'STATISTICS',
  'MEDIA',
];
const active = ref(tabs[0]);
const visited = ref([]);
const select = tab => {
  active.value = tab;
  if (!visited.value.includes(tab)) visited.value.push(tab);
};
watch([accountId, () => props.deal.id], () => {
  active.value = tabs[0];
  visited.value = [];
});
const navigate = (event, index) => {
  const target = {
    ArrowLeft: (index + tabs.length - 1) % tabs.length,
    ArrowRight: (index + 1) % tabs.length,
    Home: 0,
    End: tabs.length - 1,
  }[event.key];
  if (target === undefined) return;
  event.preventDefault();
  select(tabs[target]);
  event.currentTarget.parentElement
    .querySelectorAll('[role="tab"]')
    [target].focus();
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      role="tablist"
      :aria-label="t('KANBAN.DEAL_DETAIL')"
      class="flex gap-3 overflow-x-auto border-b border-n-weak"
    >
      <button
        v-for="(tab, index) in tabs"
        :id="`${id}-${tab}-tab`"
        :key="tab"
        type="button"
        role="tab"
        :aria-selected="active === tab"
        :aria-controls="`${id}-${tab}-panel`"
        :tabindex="active === tab ? 0 : -1"
        class="shrink-0 whitespace-nowrap border-b-2 px-1 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
        :class="
          active === tab
            ? 'border-n-blue-9 text-n-blue-11'
            : 'border-transparent text-n-slate-11'
        "
        @click="select(tab)"
        @keydown="navigate($event, index)"
      >
        {{ t(`KANBAN.CONTEXT_TABS.${tab}`) }}
      </button>
    </div>
    <div
      v-if="slots.principal"
      v-show="active === 'PRINCIPAL'"
      :id="`${id}-PRINCIPAL-panel`"
      role="tabpanel"
      :aria-labelledby="`${id}-PRINCIPAL-tab`"
      tabindex="0"
      class="flex flex-col gap-3"
    >
      <slot name="principal" />
    </div>
    <div
      v-show="active === 'HISTORY'"
      :id="`${id}-HISTORY-panel`"
      role="tabpanel"
      :aria-labelledby="`${id}-HISTORY-tab`"
      tabindex="0"
    >
      <DealTimeline :deal="deal" />
    </div>
    <div
      v-show="active === 'STATISTICS'"
      :id="`${id}-STATISTICS-panel`"
      role="tabpanel"
      :aria-labelledby="`${id}-STATISTICS-tab`"
      tabindex="0"
    >
      <DealStatistics
        v-if="visited.includes('STATISTICS')"
        :deal="deal"
        :active="active === 'STATISTICS'"
      />
    </div>
    <div
      v-show="active === 'MEDIA'"
      :id="`${id}-MEDIA-panel`"
      role="tabpanel"
      :aria-labelledby="`${id}-MEDIA-tab`"
      tabindex="0"
    >
      <DealMedia
        v-if="visited.includes('MEDIA')"
        :deal="deal"
        :active="active === 'MEDIA'"
      />
    </div>
  </div>
</template>
